import { Injectable, inject } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  WatchQueryFetchPolicy,
  ApolloLink,
  ObservableQuery,
} from '@apollo/client';
import {
  FindUniqueFormRequest,
  Form,
  InsertDataFromFormRequest,
  Question,
  QuestionType,
} from '@ih/app/shared/apis/interfaces';
import { Store } from '@ngxs/store';
import { Apollo, gql } from 'apollo-angular';
import { map, Observable } from 'rxjs';

// Where to put this?
const basicQuestionTypes = [
  QuestionType.SHORT,
  QuestionType.PARAGRAPH,
  QuestionType.DROPDOWN,
  QuestionType.BOOLEAN,
  QuestionType.DATE,
  QuestionType.TIME,
  QuestionType.NUMBER,
  QuestionType.MULTIRADIO,
  QuestionType.USERINFO,
  QuestionType.DEVICEINFO,
  QuestionType.QR,
  QuestionType.LOOKUP,
  QuestionType.JSON,
  QuestionType.TABULAR,
];

@Injectable()
export class FormsService {
  private readonly apollo = inject(Apollo);
  private readonly fb = inject(UntypedFormBuilder);
  private readonly store = inject(Store);

  extractRowColumns(
    form: Form | null,
    answers: Record<string, unknown>,
  ): {
    tableId: string;
    columnId: string;
    value: any;
  }[] {
    const rowColumns: {
      tableId: string;
      columnId: string;
      value: any;
    }[] = [];

    form?.sections?.forEach((section) => {
      section?.questions?.forEach((question) => {
        if (question?.type && basicQuestionTypes.includes(question?.type)) {
          const config = JSON.parse(question?.configuration);
          const parsedTableId = config.tableId;
          const parsedColumnId = config.columnId;

          let rawAnswer = parsedColumnId ? answers[parsedColumnId] : undefined;
          const answerEmpty =
            rawAnswer === null || rawAnswer === undefined || rawAnswer === '';
          const hasDefault =
            config.default !== undefined &&
            config.default !== null &&
            config.default !== '';
          if (answerEmpty && hasDefault) {
            rawAnswer = config.default;
          }

          if (parsedTableId && parsedColumnId && String(rawAnswer) !== 'null') {
            const isJsonQuestion =
              question?.type === QuestionType.JSON ||
              question?.type === QuestionType.TABULAR;
            let serialised: string;
            if (isJsonQuestion) {
              // JSON / TABULAR answers may be objects/arrays; stringify so
              // the DB stores valid JSON (not "[object Object]").
              if (typeof rawAnswer === 'string') {
                serialised = rawAnswer;
              } else {
                try {
                  serialised = JSON.stringify(rawAnswer ?? null);
                } catch {
                  serialised = String(rawAnswer);
                }
              }
            } else {
              serialised = rawAnswer + '';
            }
            rowColumns.push({
              tableId: parsedTableId,
              columnId: parsedColumnId,
              value: serialised,
            });
          }
        }
        if (question?.type === QuestionType.MULTICHECKBOX) {
          if (JSON.parse(question?.configuration).options) {
            JSON.parse(question?.configuration).options.forEach(
              (option: any) => {
                if (
                  option.tableId &&
                  option.columnId &&
                  answers[option.columnId]
                ) {
                  rowColumns.push({
                    tableId: option.tableId,
                    columnId: option.columnId,
                    value: answers[option.columnId] + '',
                  });
                }
              },
            );
          }
        }
        if (question?.type === QuestionType.LOCATION) {
          const parsedLatitudeTableId = JSON.parse(question?.configuration)
            .latitude?.tableId;
          const parsedLatitudeColumnId = JSON.parse(question?.configuration)
            .latitude?.columnId;
          if (
            parsedLatitudeTableId &&
            parsedLatitudeColumnId &&
            answers[parsedLatitudeColumnId]
          ) {
            rowColumns.push({
              tableId: parsedLatitudeTableId,
              columnId: parsedLatitudeColumnId,
              value: answers[parsedLatitudeColumnId] + '',
            });
          }

          const parsedLongitudeTableId = JSON.parse(question?.configuration)
            .longitude?.tableId;
          const parsedLongitudeColumnId = JSON.parse(question?.configuration)
            .longitude?.columnId;
          if (
            parsedLongitudeTableId &&
            parsedLongitudeColumnId &&
            answers[parsedLongitudeColumnId]
          ) {
            rowColumns.push({
              tableId: parsedLongitudeTableId,
              columnId: parsedLongitudeColumnId,
              value: answers[parsedLongitudeColumnId] + '',
            });
          }
        }
        if (question?.type === QuestionType.FILEUPLOAD) {
          const parsedTableId = JSON.parse(question?.configuration).tableId;
          const parsedColumnId = JSON.parse(question?.configuration).columnId;

          if (parsedTableId && parsedColumnId && answers[parsedColumnId]) {
            rowColumns.push({
              tableId: parsedTableId,
              columnId: parsedColumnId,
              value: "'" + answers[parsedColumnId] + "'" + '',
            });
          }
        }
      });
    });

    return rowColumns;
  }

  findUniqueForm(request: FindUniqueFormRequest): Observable<Form | null> {
    type ResultType = { findUniqueForm: Form | null };

    let fetchPolicy = 'cache-only';

    const networkStatus = this.store.selectSnapshot<boolean>(
      (state) => state.network.status,
    );

    if (networkStatus) {
      fetchPolicy = 'network-only';
    }

    const query = gql`
      query findUniqueForm($request: FindUniqueFormRequest!) {
        findUniqueForm(request: $request) {
          id
          updateForm
          name
          slug
          emailList
          layout
          configuration
          allowSharing
          allowDataExport
          webhook
          visibility
          sendEmail
          project {
            id
            tables {
              id
              name
              columns {
                id
                name
              }
            }
          }
          sections {
            id
            name
            order
            questions {
              id
              name
              type
              order
              configuration
              description
              content
              required
            }
          }
        }
      }
    `;

    return this.apollo
      .watchQuery<ResultType>({
        query,
        variables: {
          request,
        },
        fetchPolicy: <WatchQueryFetchPolicy>fetchPolicy,
      })
      .valueChanges.pipe(
        map((result: ObservableQuery.Result<ResultType>) => {
          if (result.data && networkStatus) {
            this.apollo.client.cache.writeQuery({
              query,
              data: result.data,
            });
          }
          return (
            (result.data?.findUniqueForm as Form | null | undefined) ?? null
          );
        }),
      );
  }

  /**
   * Creates an apollo mutation to insert the data into the db.
   * To see the offline functionality of this, see the middelware at {@offline}
   * @offline apps/app/go/src/app/graphql.module.ts
   * @param param0
   * @returns
   */
  InsertDataFromForm({
    tableId,
    rows,
    formName,
    submitterEmail,
    formId,
    projectId,
  }: InsertDataFromFormRequest): Observable<any> {
    // const projectId = this.store.selectSnapshot<Project | null>(
    //   (state) => state.project.project.id
    // );

    // const query = gql`
    //   query FindDataRows(
    //     $tableId: String!
    //     $cursor: String
    //     $limit: Int!
    //     $orderBy: String!
    //     $orderByType: DataType!
    //     $search: String
    //   ) {
    //     findDataRows(
    //       request: {
    //         tableId: $tableId
    //         cursor: $cursor
    //         limit: $limit
    //         orderBy: $orderBy
    //         orderByType: $orderByType
    //         search: $search
    //       }
    //     ) {
    //       rows {
    //         columns {
    //           columnId
    //           value
    //         }
    //       }
    //       cursor
    //     }
    //   }
    // `;

    return this.apollo
      .mutate({
        mutation: gql`
          mutation InsertDataFromForm(
            $tableId: String!
            $formName: String!
            $submitterEmail: String!
            $rows: [DataRowInput!]!
            $formId: String!
            $projectId: String!
          ) @serialize(key: ["serialiseMutation"]) {
            insertDataFromForm(
              request: {
                tableId: $tableId
                rows: $rows
                formName: $formName
                submitterEmail: $submitterEmail
                formId: $formId
                projectId: $projectId
              }
            ) {
              projectId
            }
          }
        `,
        variables: {
          tableId,
          rows,
          formName,
          submitterEmail,
          formId,
          projectId,
        },

        // TODO: adding mutation data to cache to show in table
        // - consider generic
        // - consider multiple table cache to edit

        // optimisticResponse: {
        //   __typename: 'Mutation',
        //   insertDataFromForm: {
        //     __typename: 'DataRowsResponse',
        //     projectId,
        //   },
        // },
        // update: (proxy) => {
        //   // Read the data from our cache for this query.
        //   const data = proxy.readQuery({
        //     query: query,
        //   }) as any;

        //   if (data) {
        //     console.warn(data);

        //     // Setting the primary columnm, currently only for auto increment
        //     // and retrieving data from exisitng data, need init
        //     const cursor = {
        //       columnId: data.findDataRows.rows[0].columns[0].columnId,
        //       value: (
        //         parseInt(data.findDataRows.rows[0].columns[0].value) + 1
        //       ).toString(),
        //     };

        //     // Adding the cursor
        //     rows[0].columns.unshift(cursor);

        //     // For some reason unshifting makes duplicates
        //     // Removing dups
        //     if (rows[0].columns[0].value === rows[0].columns[1].value) {
        //       rows[0].columns.splice(0, 1);
        //     }

        //     console.warn(rows);

        //     // deep copy of query data
        //     const findDataRows = {
        //       cursor: data.findDataRows.cursor,
        //       rows: data.findDataRows.rows.map((i: any) => {
        //         return i;
        //       }),
        //       __typename: 'DataRowsResponse',
        //     };

        //     console.warn(findDataRows);

        //     // Adding new data to object to cache
        //     if (findDataRows) {
        //       findDataRows.rows.unshift(rows[0]);
        //     }

        //     console.warn(findDataRows);

        //     // Write to cache
        //     proxy.updateQuery({ query }, () => ({
        //       findDataRows,
        //     }));
        //   }
        // },
      })

      .pipe(map((result) => result));
  }

  /**
   * Sends a `SubmitFormAnswers` Apollo mutation. Replaces the legacy
   * `InsertDataFromForm` call for the form-submit path so the server-side
   * pipeline (template validation → `movePendingFiles` cloud function →
   * atomic row insert) can run.
   *
   * @param formId  the form being submitted
   * @param draftId the persisted UUID for the (userId, formId) draft —
   *                must match the `<projectId>/__pending__/<draftId>/`
   *                prefix used by the file-upload widget so the server
   *                can locate the pending files
   * @param answersByTable `{ [tableId]: { [columnId]: any } }` — the
   *                       grouped answer set
   */
  SubmitFormAnswers({
    formId,
    draftId,
    answersByTable,
  }: {
    formId: string;
    draftId: string;
    answersByTable: Record<string, Record<string, unknown>>;
  }): Observable<any> {
    return this.apollo
      .mutate({
        mutation: gql`
          mutation SubmitFormAnswers(
            $formId: String!
            $draftId: String!
            $answersByTable: JSON!
          ) @serialize(key: ["serialiseMutation"]) {
            submitFormAnswers(
              request: {
                formId: $formId
                draftId: $draftId
                answersByTable: $answersByTable
              }
            ) {
              success
              error
              errorColumn
              errorDetail
            }
          }
        `,
        variables: {
          formId,
          draftId,
          answersByTable,
        },
      })
      .pipe(map((result) => result));
  }

  /**
   * Creates a untyped form group from the form layout using the configurations and types specified
   * @param formLayout$
   * @updated 18/04/2024 - 10:04:23
   * @returns
   */
  formGroupFromLayout(formLayout$: Observable<Form>): UntypedFormGroup {
    const formGroup = this.fb.group({});
    formLayout$.forEach((form) => {
      form?.sections?.forEach((section) => {
        section?.questions?.forEach((question) => {
          //TODO : Make this cleaner
          let defaultValue: unknown = '';
          if (question?.type === QuestionType.BOOLEAN) defaultValue = false;
          if (question?.type === QuestionType.JSON) defaultValue = '[]';
          if (question?.type === QuestionType.TABULAR) defaultValue = '{}';
          // If there is a question set up a formControl for it
          if (question) {
            const questionFC = new UntypedFormControl({
              value: defaultValue,
              disabled: false,
            });
            questionFC.addValidators(validatorsByType(question));

            // If the question is of the basic type add the control
            if (
              question.type &&
              (basicQuestionTypes.includes(question?.type) ||
                question?.type === 'FILEUPLOAD') &&
              JSON.parse(question.configuration).columnId
            ) {
              formGroup.addControl(
                JSON.parse(question.configuration).columnId,
                questionFC,
              );
            }
            // If the question is of the more complex type loop through options
            if (question?.type === QuestionType.MULTICHECKBOX) {
              JSON.parse(question.configuration).options.forEach(
                (option: any) => {
                  if (option.columnId) {
                    const questionOptionFC = new UntypedFormControl({
                      value: false,
                      disabled: false,
                    });
                    formGroup.addControl(option.columnId, questionOptionFC);
                  }
                },
              );
            }

            // If the question is of the Location type then add Lat and Long
            if (question?.type === QuestionType.LOCATION) {
              const editCoordId =
                JSON.parse(question.configuration).latitude.columnId +
                '_' +
                JSON.parse(question.configuration).longitude.columnId;
              formGroup.addControl(editCoordId, new UntypedFormControl(false));
              const questionLatFC = new UntypedFormControl({
                value: '',
                disabled: false,
              });
              formGroup.addControl(
                JSON.parse(question.configuration).latitude.columnId,
                questionLatFC,
              );
              const questionLongFC = new UntypedFormControl({
                value: '',
                disabled: false,
              });
              formGroup.addControl(
                JSON.parse(question.configuration).longitude.columnId,
                questionLongFC,
              );
            }
          }
        });
      });
    });
    return formGroup;
  }
}

/**
 * Configures the validators for the form based on the question type and configuration specified
 * Note : Regex is not implemented for all types, and does not work for NUMBER type.
 * TODO : Fix or remove Number regex
 * @updated 18/04/2024 - 10:01:45
 * @param question
 * @returns
 */
function validatorsByType(question: Question): ValidatorFn[] {
  const configArray = JSON.parse(question.configuration);
  const validatorArray = [];
  switch (question.type) {
    case 'SHORT': {
      if (
        typeof configArray.minLength === 'number' &&
        configArray.minLength > 0
      ) {
        validatorArray.push(Validators.minLength(configArray.minLength));
      }
      if (
        typeof configArray.maxLength === 'number' &&
        configArray.maxLength > 0
      ) {
        validatorArray.push(Validators.maxLength(configArray.maxLength));
      }
      if (configArray.pattern) {
        validatorArray.push(Validators.pattern(configArray.pattern));
      }
      break;
    }
    case 'PARAGRAPH': {
      if (
        typeof configArray.minLength === 'number' &&
        configArray.minLength > 0
      ) {
        validatorArray.push(Validators.minLength(configArray.minLength));
      }
      if (
        typeof configArray.maxLength === 'number' &&
        configArray.maxLength > 0
      ) {
        validatorArray.push(Validators.maxLength(configArray.maxLength));
      }
      break;
    }
    case 'NUMBER': {
      const numericReg = configArray.integer
        ? '^-?\\d+$'
        : '^-?\\d+(\\.\\d+)?$';
      validatorArray.push(Validators.pattern(numericReg));
      const max = parseFloat(configArray.max);
      const min = parseFloat(configArray.min);
      if (!isNaN(max)) validatorArray.push(Validators.max(max));
      if (!isNaN(min)) validatorArray.push(Validators.min(min));
      break;
    }
    case 'DROPDOWN': {
      // NOTE: an allowed-value validator was attempted here but reverted
      // because option storage may not match the saved control value
      // (label vs value vs id). Revisit once option shape is unified.
      break;
    }
    case 'DATE': {
      if (configArray.min) {
        validatorArray.push(dateMinValidator(configArray.min));
      }
      if (configArray.max) {
        validatorArray.push(dateMaxValidator(configArray.max));
      }
      break;
    }
    case 'TIME': {
      // NOTE: time pattern validator removed for now; existing values may
      // not all be zero-padded HH:mm. Add back once value format is verified.
      break;
    }
    case 'BOOLEAN': {
      break;
    }
    case 'MULTIRADIO': {
      break;
    }
    case 'MULTICHECKBOX': {
      break;
    }
    case 'FILEUPLOAD': {
      // NOTE: required-file validator removed for now; uploaded value may
      // be a URL string or a complex object depending on flow. Revisit
      // once the file value shape is standardised.
      break;
    }
    case 'TABULAR': {
      validatorArray.push(tabularRequiredCellsValidator(configArray));
      break;
    }
  }
  if (question.required) {
    validatorArray.push(Validators.required);
  }
  return validatorArray;
}

function dateMinValidator(min: string): ValidatorFn {
  return (control) => {
    const v = control?.value;
    if (!v) return null;
    return new Date(v) < new Date(min) ? { dateMin: { min } } : null;
  };
}

function dateMaxValidator(max: string): ValidatorFn {
  return (control) => {
    const v = control?.value;
    if (!v) return null;
    return new Date(v) > new Date(max) ? { dateMax: { max } } : null;
  };
}

function tabularRequiredCellsValidator(config: any): ValidatorFn {
  const rows: { name: string; required?: boolean }[] = config?.rows ?? [];
  const cols: { name: string; required?: boolean }[] = config?.cols ?? [];
  const requiredRows = rows.filter((r) => r?.required).map((r) => r.name);
  const requiredCols = cols.filter((c) => c?.required).map((c) => c.name);
  if (requiredRows.length === 0 && requiredCols.length === 0) {
    return () => null;
  }
  return (control) => {
    const raw = control?.value;
    if (raw === undefined || raw === null || raw === '') {
      return { tabularRequired: true };
    }
    let parsed: any;
    try {
      parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    } catch {
      return { tabularRequired: true };
    }
    if (!parsed || typeof parsed !== 'object') {
      return { tabularRequired: true };
    }
    const isEmpty = (v: any) =>
      v === undefined ||
      v === null ||
      v === '' ||
      (typeof v === 'number' && isNaN(v));
    for (const rowName of requiredRows) {
      const rowData = parsed[rowName] ?? {};
      for (const col of cols) {
        if (isEmpty(rowData[col.name])) return { tabularRequired: true };
      }
    }
    for (const colName of requiredCols) {
      for (const row of rows) {
        const rowData = parsed[row.name] ?? {};
        if (isEmpty(rowData[colName])) return { tabularRequired: true };
      }
    }
    return null;
  };
}
